import { Badge } from '@/components/ui/badge';
import { Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFlooActiveScopeContext } from '@/hooks/useFlooActiveScopeContext';

/**
 * Floo Network Phase 8 — small, consistent scope indicator shown at
 * the top of any dashboard wired to the active scope. Renders nothing
 * when there's no resolvable scope (loading or unauthenticated state).
 *
 * The component is intentionally minimal:
 *   • One line of read-only state.
 *   • No interactivity (the ScopeSwitcher in TopBar is where the user
 *     changes scope).
 *   • Shows whether the scope is user-chosen (`isActiveScopeReady`)
 *     vs. the membership-derived fallback so admins can see at a
 *     glance whether the dashboard reflects their switcher selection
 *     or their default membership scope.
 */
export function ActiveScopeBadge({
  className,
  testId,
}: {
  className?: string;
  testId?: string;
}) {
  const ctx = useFlooActiveScopeContext();

  if (!ctx.scopeType) return null;

  // Fallback case (`useEnterpriseScope.scopeLabel` is just the level
  // word, e.g. 'Organization') would otherwise render the level twice:
  //   "Viewing: [Organization] Organization · membership default"
  // Suppress the trailing entity span when it duplicates the badge.
  // When active scope IS ready, `ctx.label` is the actual entity name
  // ("Sun Auto") so they differ and the span renders normally.
  const showEntityLabel = ctx.label.trim() !== ctx.levelLabel.trim();

  return (
    <div
      className={cn(
        'flex items-center gap-2 text-xs text-muted-foreground mb-3',
        className,
      )}
      data-testid={testId ?? 'floo-active-scope-badge'}
    >
      <Eye className="h-3 w-3" />
      <span>Viewing:</span>
      <Badge variant="outline" className="text-[10px]">
        {ctx.levelLabel}
      </Badge>
      {showEntityLabel && (
        <span className="font-medium text-foreground">{ctx.label}</span>
      )}
      {!ctx.isActiveScopeReady && (
        <span className="italic text-muted-foreground">
          · membership default
        </span>
      )}
    </div>
  );
}
